import React, { useCallback, useState } from "react";
import { animated, useSpring } from "react-spring";
import { bioPink } from "../constants/constants";
import { springConfigs } from "../constants/springConfigs";

const TransparentCurtain = ({
  horizontal = false,
  toLeft = false,
}: {
  horizontal: boolean;
  toLeft: boolean;
}) => {
  const [intro, setIntro] = useState(true);

  const getClipPathSpring = useCallback(() => {
    if (intro && !horizontal) {
      return {
        // hide all from the top
        from: { TransparentCurtainClipPath: "inset(100% 0% 0% 0%)" },
        // show all
        to: { TransparentCurtainClipPath: "inset(0% 0% 0% 0%)" },
        config: springConfigs.curtains.introTransparent,
        onRest: () => setIntro(false),
      };
    }

    if (!intro && !horizontal) {
      return {
        // show all
        from: { TransparentCurtainClipPath: "inset(0% 0% 0% 0%)" },
        // hide all bottom
        to: { TransparentCurtainClipPath: "inset(0% 0% 100% 0%)" },
        config: springConfigs.curtains.outro,
      };
    }

    if (intro && horizontal && !toLeft) {
      return {
        // hide all from the left
        from: { TransparentCurtainClipPath: "inset(0% 0% 0% 100%)" },
        // show all
        to: { TransparentCurtainClipPath: "inset(0% 0% 0% 0%)" },
        config: springConfigs.curtains.introTransparent,
        onRest: () => setIntro(false),
      };
    }

    if (!intro && horizontal && !toLeft) {
      return {
        // show all
        from: { TransparentCurtainClipPath: "inset(0% 0% 0% 0%)" },
        // hide all from the right
        to: { TransparentCurtainClipPath: "inset(0% 100% 0% 0%)" },
        config: springConfigs.curtains.outro,
      };
    }

    if (intro && horizontal && toLeft) {
      return {
        // hide all from the left
        from: { TransparentCurtainClipPath: "inset(0% 100% 0% 0%)" },
        // show all
        to: { TransparentCurtainClipPath: "inset(0% 0% 0% 0%)" },
        config: springConfigs.curtains.introTransparent,
        onRest: () => setIntro(false),
      };
    }

    if (!intro && horizontal && toLeft) {
      return {
        // show all
        from: { TransparentCurtainClipPath: "inset(0% 0% 0% 0%)" },
        // hide all from the right
        to: { TransparentCurtainClipPath: "inset(0% 0% 0% 100%)" },
        config: springConfigs.curtains.outro,
      };
    }
  }, [horizontal, intro, toLeft]);

  const { TransparentCurtainClipPath } = useSpring({ ...getClipPathSpring() });

  return (
    <animated.div
      {...{
        style: {
          gridArea: "AppContentContainer",
          width: "100%",
          height: "100%",
          backgroundColor: bioPink,
          clipPath: TransparentCurtainClipPath,
          opacity: 0.25,
          zIndex: 99999,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: "none",
        },
      }}
    />
  );
};

const OpaqueCurtain = ({
  onCurtainHides,
  onCurtainEnd,
  horizontal = false,
  toLeft = false,
}: {
  onCurtainHides: any;
  onCurtainEnd: any;
  horizontal?: boolean;
  toLeft?: boolean;
}) => {
  // console.log({ horizontal });

  const [intro, introSet] = useState(true);

  // useCallback: don't compute every time
  const getClipPathSpring = useCallback(() => {
    if (intro && !horizontal) {
      return {
        // hide all from the top
        from: { OpaqueCurtainClipPath: "inset(100% 0% 0% 0%)" },
        // show all
        to: { OpaqueCurtainClipPath: "inset(0% 0% 0% 0%)" },
        config: springConfigs.curtains.intro,
        onRest: () => {
          introSet(false);
          onCurtainHides();
        },
        delay: 360,
        // delay: 360,
      };
    }

    if (!intro && !horizontal) {
      return {
        // show all
        from: { OpaqueCurtainClipPath: "inset(0% 0% 0% 0%)" },
        // hide all bottom
        to: { OpaqueCurtainClipPath: "inset(0% 0% 100% 0%)" },
        config: springConfigs.curtains.outro,
        onRest: () => {
          onCurtainEnd();
        },
      };
    }

    if (intro && horizontal && !toLeft) {
      return {
        // hide all from the left
        from: { OpaqueCurtainClipPath: "inset(0% 0% 0% 100%)" },
        // show all
        to: { OpaqueCurtainClipPath: "inset(0% 0% 0% 0%)" },
        config: springConfigs.curtains.intro,
        onRest: () => {
          introSet(false);
          onCurtainHides();
        },
        delay: 300,
      };
    }

    if (!intro && horizontal && !toLeft) {
      return {
        // show all
        from: { OpaqueCurtainClipPath: "inset(0% 0% 0% 0%)" },
        // hide all from the right
        to: { OpaqueCurtainClipPath: "inset(0% 100% 0% 0%)" },
        config: springConfigs.curtains.outro,
        onRest: () => {
          onCurtainEnd();
        },
      };
    }

    if (intro && horizontal && toLeft) {
      return {
        // hide all from the left
        from: { OpaqueCurtainClipPath: "inset(0% 100% 0% 0%)" },
        // show all
        to: { OpaqueCurtainClipPath: "inset(0% 0% 0% 0%)" },
        config: springConfigs.curtains.intro,
        onRest: () => {
          introSet(false);
          onCurtainHides();
        },
        delay: 300,
      };
    }

    if (!intro && horizontal && toLeft) {
      return {
        // show all
        from: { OpaqueCurtainClipPath: "inset(0% 0% 0% 0%)" },
        // hide all from the right
        to: { OpaqueCurtainClipPath: "inset(0% 0% 0% 100%)" },
        config: springConfigs.curtains.outro,
        onRest: () => {
          onCurtainEnd();
        },
      };
    }
  }, [intro, horizontal, toLeft, onCurtainHides, onCurtainEnd]);

  const { OpaqueCurtainClipPath } = useSpring({ ...getClipPathSpring() });

  return (
    <>
      <animated.div
        {...{
          style: {
            gridArea: "AppContentContainer",
            display: "flex",
            width: "100%",
            height: "100%",
            backgroundColor: bioPink,
            clipPath: OpaqueCurtainClipPath,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "none",
            zIndex: 99999,
          },
        }}
      >
        <div
          style={{
            color: "#BD113C",
            fontSize: "5rem",
            fontWeight: 600,
          }}
        >
          IOCO
        </div>
        <div
          style={{
            color: "#D91041",
            fontSize: "0.6rem",
            fontWeight: 900,
            marginTop: "-0.5em",
            letterSpacing: "0.5rem",
          }}
        >
          BIOCOMPUTER
        </div>
      </animated.div>
    </>
  );
};

const Curtain = ({
  onCurtainHides,
  onCurtainEnd,
  horizontal = false,
  toLeft = false,
}: {
  onCurtainHides: any;
  onCurtainEnd: any;
  horizontal?: boolean;
  toLeft?: boolean;
}) => {
  // console.log({
  //   toLeft,
  // });

  return (
    <>
      <TransparentCurtain {...{ horizontal, toLeft }} />
      <OpaqueCurtain
        {...{
          onCurtainHides,
          onCurtainEnd,
          horizontal,
          toLeft,
        }}
      />
    </>
  );
};

export { Curtain };
